import extend from './extend';
import Raven from 'raven-js';

// Used to keep references to wrapped handlers from original handlers.
const wrappedHandlerMap = new WeakMap();

const parseNamespacedEventName = (eventName) => {
    const parts = eventName.split('.');

    if (parts.length === 1) {
        return {
            namespace: 'all',
            eventName
        };
    }

    if (parts.length === 2) {
        return {
            namespace: parts[0],
            eventName: parts[1]
        };
    }

    throw new Error('Bad event name');
};

/**
 * We wrap event handlers with a try-catch so that we are able to report any
 * uncaught exceptions.
 */
const wrapWithErrorHandler = (fn) => (...args) => {
    try {
        fn(...args);
    } catch(err) {
        if (Raven.isSetup()) {
            Raven.captureException(err);
        }

        /*
         * Opinionated: we do not re-throw the error here, instead we consider
         * it reported and thus handled. By doing this we prevent the error to
         * bubble up to the client site, so that the error is not reported
         * again by site's own error handler/reporter.
         *
         * However, for debugging purposes it is useful if the error is shown
         * in the console. We do report the error with Debug.error because
         * this is not really a caught error.
         */
        if (console) {
            console.error(err); // eslint-disable-line no-console
        }
    }
};

export const createEventWrapper = () => ({
    events: {},
    addEventSVP: function (namespacedEventName, handler) {
        if (!this.addEventListener) {
            return;
        }

        const { namespace, eventName } = parseNamespacedEventName(namespacedEventName);
        const wrappedHandler = wrapWithErrorHandler(handler);

        wrappedHandlerMap.set(handler, wrappedHandler);

        this.events[namespace] = this.events[namespace] || {};
        this.events[namespace][eventName] = this.events[namespace][eventName] || { handlers: [] };
        this.events[namespace][eventName].handlers.push(wrappedHandler);

        this.addEventListener(eventName, wrappedHandler);
    },
    removeEventSVP: function (namespacedEventName, handler) {
        if (!this.removeEventListener) {
            return;
        }

        const { namespace, eventName } = parseNamespacedEventName(namespacedEventName);

        if (!this.events[namespace]) {
            return;
        }

        if (handler) {
            const wrappedHandler = wrappedHandlerMap.get(handler);
            if (wrappedHandler) {
                this.removeEventListener(eventName, wrappedHandler);
            }

            this.removeEventListener(eventName, handler);
        } else if(this.events[namespace][eventName]) {
            this.events[namespace][eventName].handlers.forEach((handler) => {
                this.removeEventListener(eventName, handler);
            });
        }

        delete this.events[namespace][eventName];
    },
    destroy: function () {
        if (this.removeEventListener) {
            for (var namespace in this.events) {
                if (Object.prototype.hasOwnProperty.call(this.events, namespace)) {
                    for (var event in this.events[namespace]) {
                        if (Object.prototype.hasOwnProperty.call(this.events[namespace], event) && this.events[namespace][event]) {
                            this.events[namespace][event].handlers.forEach(this.removeEventListener.bind(this, event));
                        }
                    }
                }
            }

        }
        this.addEventSVP = undefined;
        this.removeEventSVP = undefined;
        this.events = undefined;
    }
});




// WEBPACK FOOTER //
// ./src/js/util/event-wrapper-api.js